import type { LucideIcon } from 'lucide-react';
import {
  LayoutDashboard,
  ListTodo,
  FileText,
  TestTube,
  ClipboardList,
  Settings,
  CalendarRange,
  MessageCircle,
  RefreshCw,
  GitPullRequest,
  Network,
  BarChart3,
  Tag,
  Palette,
  FolderOpen,
} from 'lucide-react';

export interface ProjectMenuItem {
  key: string;
  label: string;
  to: string;        // NavLink 상대 경로
  icon: LucideIcon;
  end?: boolean;      // NavLink end prop (index 라우트용)
}

/**
 * 전체 메뉴 항목 (탭 순서대로)
 */
const ALL_MENU_ITEMS: ProjectMenuItem[] = [
  { key: 'dashboard', label: '대시보드', to: 'dashboard', icon: LayoutDashboard },
  { key: 'schedule', label: '일정', to: 'schedule', icon: CalendarRange },
  { key: 'sprint', label: '스프린트', to: 'sprint', icon: RefreshCw },
  { key: 'requirements', label: '요구사항', to: 'requirements', icon: ListTodo },
  { key: 'specs', label: '명세', to: 'specifications', icon: FileText },
  { key: 'testcases', label: '테스트', to: 'tests', icon: TestTube },
  { key: 'issues', label: '작업', to: 'issues', icon: ClipboardList },
  { key: 'design', label: '디자인', to: 'design-items', icon: Palette },
  { key: 'code-review', label: '코드 리뷰', to: 'code-review', icon: GitPullRequest },
  { key: 'traceability', label: '추적성', to: 'traceability', icon: Network },
  { key: 'metrics', label: '메트릭', to: 'metrics', icon: BarChart3 },
  { key: 'releases', label: '릴리즈', to: 'releases', icon: Tag },
  { key: 'chat', label: '채팅', to: 'chat', icon: MessageCircle },
  // CHG-130 REQ-DHR-025
  { key: 'documents', label: '문서함', to: 'documents', icon: FolderOpen },
  { key: 'settings', label: '설정', to: 'settings', icon: Settings },
];

/**
 * 프로젝트 유형별 허용 탭 키 목록
 */
export const PROJECT_MENU_KEYS: Record<string, string[]> = {
  // CHG-130 REQ-DHR-025: chat과 settings 사이에 documents 추가
  SCRUM: ['dashboard', 'schedule', 'sprint', 'requirements', 'specs', 'testcases', 'issues', 'design', 'code-review', 'traceability', 'metrics', 'releases', 'chat', 'documents', 'settings'],
  KANBAN: ['dashboard', 'schedule', 'requirements', 'specs', 'testcases', 'issues', 'design', 'code-review', 'traceability', 'metrics', 'releases', 'chat', 'documents', 'settings'],
  CS: ['dashboard', 'issues', 'design', 'code-review', 'traceability', 'metrics', 'releases', 'chat', 'documents', 'settings'],
  TEAM: ['dashboard', 'sprint', 'issues', 'design', 'code-review', 'traceability', 'metrics', 'releases', 'chat', 'documents', 'settings'],
};

/**
 * CHG-055 REQ-V55-004: 토글 가능 메뉴 정의
 */
export interface ToggleableMenu {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  fixed?: boolean;
}

export const TOGGLEABLE_MENUS: ToggleableMenu[] = [
  { key: 'dashboard', label: '대시보드', description: '프로젝트 개요 및 통계', icon: LayoutDashboard, fixed: true },
  { key: 'schedule', label: '일정', description: 'WBS 기반 일정 관리', icon: CalendarRange },
  { key: 'sprint', label: '스프린트', description: '스프린트 계획 및 관리', icon: RefreshCw },
  { key: 'requirements', label: '요구사항', description: '요구사항 정의 및 추적', icon: ListTodo },
  { key: 'specs', label: '명세', description: '기능 명세서 관리', icon: FileText },
  { key: 'testcases', label: '테스트', description: '테스트 케이스 및 실행', icon: TestTube },
  { key: 'issues', label: '작업', description: '이슈 및 작업 관리', icon: ClipboardList },
  { key: 'design', label: '디자인', description: '디자인 파일 및 Figma 연동', icon: Palette },
  { key: 'code-review', label: '코드 리뷰', description: 'GitHub PR 리뷰 관리', icon: GitPullRequest },
  { key: 'traceability', label: '추적성', description: '요구사항-명세-테스트 추적', icon: Network },
  { key: 'metrics', label: '메트릭', description: '프로젝트 지표 및 분석', icon: BarChart3 },
  { key: 'releases', label: '릴리즈', description: '릴리즈 관리', icon: Tag },
  { key: 'chat', label: '채팅', description: '프로젝트 내 실시간 채팅', icon: MessageCircle },
  // CHG-130 REQ-DHR-025: documents는 fixed (사용자가 끌 수 없음 — 데일리 스크럼 자동 저장 필수 의존)
  { key: 'documents', label: '문서함', description: '프로젝트 회의록 및 공유 문서', icon: FolderOpen, fixed: true },
  { key: 'settings', label: '설정', description: '프로젝트 설정', icon: Settings, fixed: true },
];

/**
 * 라우트 경로 segment → 메뉴 키 매핑.
 * URL의 첫 번째 path segment를 메뉴 키로 변환한다.
 * (예: 'specifications' → 'specs', 'tests' → 'testcases')
 */
const ROUTE_TO_MENU_KEY: Record<string, string> = {
  dashboard: 'dashboard',
  schedule: 'schedule',
  sprint: 'sprint',
  requirements: 'requirements',
  specifications: 'specs',
  tests: 'testcases',
  'test-results': 'testcases', // REQ-V32-001: 상단탭 제거 후 테스트 서브탭으로 리다이렉트 허용
  issues: 'issues',
  settings: 'settings',
  chat: 'chat',
  specs: 'specs', // /specs/:specId 경로 대응
  'design-items': 'design', // CHG-049
  'code-review': 'code-review', // CHG-047
  traceability: 'traceability', // CHG-047
  metrics: 'metrics', // CHG-048
  releases: 'releases', // CHG-048
  documents: 'documents', // CHG-130
};

// CHG-130 REQ-DHR-025: 모든 프로젝트에서 항상 노출되어야 하는 fixed 메뉴
// TOGGLEABLE_MENUS.fixed=true 와 일치해야 한다.
const ALWAYS_ENABLED_KEYS = ['dashboard', 'documents', 'settings'];

function withFixedMenus(keys: string[]): string[] {
  const missing = ALWAYS_ENABLED_KEYS.filter((k) => !keys.includes(k));
  return missing.length === 0 ? keys : [...keys, ...missing];
}

/**
 * 현재 라우트 경로가 해당 프로젝트 유형에서 허용되는지 확인한다.
 * @param pathSegment - URL의 feature path segment (예: 'specifications', 'tests')
 * @param projectType - 프로젝트 유형 (예: 'CS', 'SCRUM')
 * @returns true면 허용, false면 차단
 */
export function isFeatureAllowed(pathSegment: string, projectType: string, enabledMenus?: string[] | null): boolean {
  const menuKey = ROUTE_TO_MENU_KEY[pathSegment];
  if (!menuKey) return true; // 매핑 없는 경로는 통과 (index 등)
  const allowed = withFixedMenus(enabledMenus ?? PROJECT_MENU_KEYS[projectType] ?? PROJECT_MENU_KEYS.SCRUM);
  return allowed.includes(menuKey);
}

/**
 * 프로젝트의 활성 메뉴 항목을 반환한다.
 * enabledMenus가 있으면 이를 필터 기준으로, 없으면 PROJECT_MENU_KEYS fallback.
 * fixed 메뉴(dashboard/documents/settings)는 enabledMenus에 없어도 강제로 포함된다.
 */
export function getProjectMenuItems(projectType: string, options?: { enabledMenus?: string[] | null; chatEnabled?: boolean; hasGitHubRepos?: boolean }): ProjectMenuItem[] {
  const keys = withFixedMenus(options?.enabledMenus ?? PROJECT_MENU_KEYS[projectType] ?? PROJECT_MENU_KEYS.SCRUM);
  return ALL_MENU_ITEMS.filter((item) => {
    if (!keys.includes(item.key)) return false;
    if (item.key === 'code-review' && options?.hasGitHubRepos === false) return false;
    if (item.key === 'metrics' && options?.hasGitHubRepos === false) return false;
    return true;
  });
}
